import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Assignment, Build, Delete, Devices, Refresh, Update } from '@mui/icons-material'
import { deviceService, serviceVisitService } from '../services/api'
import { useStore } from '../store/authStore'
import { CreateServiceVisitPayload, DeviceRecord, ServiceVisitRecord } from '../types'

type VisitFormState = {
  deviceId: string
  visitType: 'Activation' | 'Update' | 'Maintenance' | 'PrinterInstall' | 'Other'
  description: string
  newVersion: string
  price: string
  visitDate: string
}

const emptyForm: VisitFormState = {
  deviceId: '',
  visitType: 'Maintenance',
  description: '',
  newVersion: '',
  price: '',
  visitDate: new Date().toISOString().slice(0, 16),
}

const unwrapData = <T,>(response: any): T => (response?.data ?? response) as T

const formatDate = (value: string) =>
  new Date(value).toLocaleString('ar-IQ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const getVisitTypeLabel = (visitType: ServiceVisitRecord['visitType']) => {
  switch (visitType) {
    case 'Activation':
      return 'تفعيل'
    case 'Update':
      return 'تحديث'
    case 'Maintenance':
      return 'صيانة'
    case 'PrinterInstall':
      return 'تنصيب طابعة'
    default:
      return 'أخرى'
  }
}

const getVisitTypeColor = (visitType: ServiceVisitRecord['visitType']) => {
  switch (visitType) {
    case 'Activation':
      return 'success'
    case 'Update':
      return 'info'
    case 'Maintenance':
      return 'warning'
    case 'PrinterInstall':
      return 'secondary'
    default:
      return 'default'
  }
}

export default function ServiceVisitsPage() {
  const { user } = useStore()
  const [visits, setVisits] = useState<ServiceVisitRecord[]>([])
  const [devices, setDevices] = useState<DeviceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | ServiceVisitRecord['visitType']>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<VisitFormState>(emptyForm)

  const fetchPageData = async () => {
    try {
      setLoading(true)
      const [visitsResponse, devicesResponse] = await Promise.all([
        serviceVisitService.getAll(),
        deviceService.getAll(),
      ])

      setVisits(unwrapData<ServiceVisitRecord[]>(visitsResponse) ?? [])
      setDevices(unwrapData<DeviceRecord[]>(devicesResponse) ?? [])
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل في تحميل زيارات الخدمة' })
      setVisits([])
      setDevices([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPageData()
  }, [])

  const filteredVisits = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return visits.filter((visit) => {
      const matchesSearch = !query
        || [visit.customerName, visit.deviceCode, visit.technicianName, visit.description]
          .join(' ')
          .toLowerCase()
          .includes(query)

      const matchesType = filterType === 'all' || visit.visitType === filterType
      return matchesSearch && matchesType
    })
  }, [visits, searchTerm, filterType])

  const totalRevenue = filteredVisits.reduce((sum, visit) => sum + (visit.price || 0), 0)
  const selectedDevice = devices.find((device) => device.id === form.deviceId) || null

  const submitVisit = async () => {
    if (!form.deviceId || !form.description.trim()) {
      setFeedback({ severity: 'error', message: 'اختيار الجهاز وكتابة الوصف مطلوبان' })
      return
    }

    try {
      setSaving(true)
      const payload: CreateServiceVisitPayload = {
        deviceId: form.deviceId,
        visitType: form.visitType,
        description: form.description.trim(),
        newVersion: form.newVersion.trim() || undefined,
        price: form.price ? Number(form.price) : undefined,
        visitDate: form.visitDate ? new Date(form.visitDate).toISOString() : undefined,
      }

      await serviceVisitService.create(payload)
      setFeedback({ severity: 'success', message: 'تم تسجيل الزيارة بنجاح' })
      setDialogOpen(false)
      setForm(emptyForm)
      await fetchPageData()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر تسجيل الزيارة' })
    } finally {
      setSaving(false)
    }
  }

  const deleteVisit = async (visit: ServiceVisitRecord) => {
    if (!window.confirm(`هل تريد حذف زيارة ${visit.deviceCode}؟`)) return

    try {
      await serviceVisitService.delete(visit.id)
      setFeedback({ severity: 'success', message: 'تم حذف الزيارة بنجاح' })
      await fetchPageData()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر حذف الزيارة' })
    }
  }

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            زيارات الخدمة والصيانة
          </Typography>
          <Typography color="text.secondary">
            من هنا تسجل زيارة تفعيل أو صيانة أو تحديث على الجهاز المرتبط مباشرة بالعميل.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPageData} disabled={loading}>
            تحديث
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)} disabled={devices.length === 0}>
            تسجيل زيارة
          </Button>
        </Stack>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 3, borderRadius: 2 }}>
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary">إجمالي الزيارات</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{filteredVisits.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary">زيارات الصيانة</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {filteredVisits.filter((visit) => visit.visitType === 'Maintenance').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography color="text.secondary">الإيرادات المسجلة</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{totalRevenue.toLocaleString('ar-IQ')}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="بحث بالعميل أو الجهاز أو الفني أو الوصف"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="نوع الزيارة"
              value={filterType}
              onChange={(event) => setFilterType(event.target.value as 'all' | ServiceVisitRecord['visitType'])}
            >
              <MenuItem value="all">كل الأنواع</MenuItem>
              <MenuItem value="Activation">تفعيل</MenuItem>
              <MenuItem value="Update">تحديث</MenuItem>
              <MenuItem value="Maintenance">صيانة</MenuItem>
              <MenuItem value="PrinterInstall">تنصيب طابعة</MenuItem>
              <MenuItem value="Other">أخرى</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredVisits.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Assignment sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>لا توجد زيارات حالياً</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            اختر جهازاً من العميل ثم سجل أول زيارة صيانة أو تفعيل أو تحديث.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)} disabled={devices.length === 0}>
            تسجيل أول زيارة
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>التاريخ</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>العميل</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الجهاز</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>نوع الزيارة</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الفني</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الوصف</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>المبلغ</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVisits.map((visit) => (
                  <TableRow key={visit.id} hover>
                    <TableCell>{formatDate(visit.visitDate)}</TableCell>
                    <TableCell>{visit.customerName}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{visit.deviceCode}</Typography>
                        {visit.newVersion && (
                          <Typography variant="caption" color="text.secondary">إصدار جديد: {visit.newVersion}</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={getVisitTypeLabel(visit.visitType)} color={getVisitTypeColor(visit.visitType) as any} size="small" />
                    </TableCell>
                    <TableCell>{visit.technicianName}</TableCell>
                    <TableCell sx={{ maxWidth: 340 }}>
                      <Typography
                        variant="body2"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {visit.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{visit.price ? visit.price.toLocaleString('ar-IQ') : '-'}</TableCell>
                    <TableCell>
                      {user?.role === 'Owner' ? (
                        <Button size="small" color="error" startIcon={<Delete />} onClick={() => deleteVisit(visit)}>
                          حذف
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">عرض فقط</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>تسجيل زيارة جديدة</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="الجهاز"
              value={form.deviceId}
              onChange={(event) => setForm((prev) => ({ ...prev, deviceId: event.target.value }))}
              fullWidth
              required
            >
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.customerName} - {device.deviceCode}
                </MenuItem>
              ))}
            </TextField>

            {selectedDevice && (
              <Alert severity="info">
                العميل: {selectedDevice.customerName} | الجهاز: {selectedDevice.deviceCode} | النوع: {selectedDevice.type === 'Main' ? 'رئيسي' : 'فرعي'}
              </Alert>
            )}

            <TextField
              select
              label="نوع الزيارة"
              value={form.visitType}
              onChange={(event) => setForm((prev) => ({ ...prev, visitType: event.target.value as VisitFormState['visitType'] }))}
              fullWidth
            >
              <MenuItem value="Activation">تفعيل</MenuItem>
              <MenuItem value="Update">تحديث</MenuItem>
              <MenuItem value="Maintenance">صيانة</MenuItem>
              <MenuItem value="PrinterInstall">تنصيب طابعة</MenuItem>
              <MenuItem value="Other">أخرى</MenuItem>
            </TextField>

            <TextField
              label="تفاصيل الزيارة"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              multiline
              minRows={4}
              fullWidth
              required
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="الإصدار الجديد"
                  value={form.newVersion}
                  onChange={(event) => setForm((prev) => ({ ...prev, newVersion: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="المبلغ"
                  type="number"
                  value={form.price}
                  onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="تاريخ ووقت الزيارة"
                  type="datetime-local"
                  value={form.visitDate}
                  onChange={(event) => setForm((prev) => ({ ...prev, visitDate: event.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={submitVisit} disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ الزيارة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
